/* =========================================================
   JuroMiru landing - behaviours ported from the approved page
   - lazy hero video autoplay (muted/loop)
   - reveal-on-scroll (no flicker)
   - email signup -> Klaviyo client subscribe (consent)
   - brand partner contact form -> smooth local thank-you transition
   Scoped to .jm-root markup. Safe to load on every page.
   ========================================================= */
(function () {
  'use strict';
  if (window.__jmLandingInit) return;
  window.__jmLandingInit = true;

  /* ---- Hero video: mobile-safe muted autoplay ---- */
  function getHeroVideos() {
    return Array.prototype.slice.call(document.querySelectorAll('.jm-hero .va-hero-media video'));
  }

  function prepareHeroVideo(video) {
    var lazySrc = video.dataset && video.dataset.src;
    var attrSrc = typeof video.getAttribute === 'function' ? video.getAttribute('src') : '';
    var source = video.querySelector('source[src]') || video.querySelector('source');
    var sourceSrc = source && (source.getAttribute ? source.getAttribute('src') : source.src);

    if (lazySrc && !attrSrc && !video.src) {
      video.src = lazySrc;
      video.setAttribute('src', lazySrc);
      video.load();
    } else if (sourceSrc && !attrSrc && !video.src) {
      video.src = sourceSrc;
      video.setAttribute('src', sourceSrc);
    }

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.loop = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.setAttribute('preload', 'auto');
  }

  function requestHeroVideoPlay(video) {
    prepareHeroVideo(video);
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () { /* autoplay blocked - retry on mobile lifecycle events */ });
  }

  function bindHeroVideo(video) {
    if (video.dataset && video.dataset.jmHeroVideoBound === '1') return;
    if (video._jmHeroVideoBound) return;

    if (video.dataset) video.dataset.jmHeroVideoBound = '1';
    video._jmHeroVideoBound = true;

    ['loadedmetadata', 'loadeddata', 'canplay'].forEach(function (eventName) {
      video.addEventListener(eventName, function () { requestHeroVideoPlay(video); }, { passive: true });
    });
  }

  function playHeroVideos() {
    getHeroVideos().forEach(function (video) {
      bindHeroVideo(video);
      requestHeroVideoPlay(video);
    });
  }

  function initVideo() {
    playHeroVideos();
    window.setTimeout(playHeroVideos, 300);
    window.setTimeout(playHeroVideos, 1200);
  }

  window.addEventListener('load', playHeroVideos, { once: true });
  window.addEventListener('pageshow', playHeroVideos);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) playHeroVideos();
  });
  document.addEventListener('touchstart', playHeroVideos, { once: true, passive: true });

  /* ---- Reveal on scroll: in-viewport reveal synchronously, rest via IO ---- */
  var io;
  function isInViewport(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top < vh && r.bottom > 0;
  }
  function observeReveals() {
    var items = Array.prototype.slice.call(document.querySelectorAll('.jm-root .reveal'));
    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    if (io) io.disconnect();
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('in'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    requestAnimationFrame(function () {
      items.forEach(function (el) {
        if (isInViewport(el)) el.classList.add('in');
        else io.observe(el);
      });
    });
  }

  /* ---- Form status message ---- */
  function showMsg(id, text, ok) {
    var el = document.querySelector('.form-msg[data-msg-for="' + id + '"]');
    if (!el) return;
    el.textContent = text;
    el.classList.add('show');
    el.classList.toggle('err', !ok);
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.classList.remove('show'); }, 8000);
  }

  /* ---- Email signup -> Klaviyo list subscribe ----
     Uses Klaviyo's list-subscribe endpoint with a form-encoded body. That makes
     it a CORS "simple" request (no custom headers), so the browser sends NO
     preflight - which means Klaviyo's DataDome bot-protection can't block the
     OPTIONS preflight and real visitors subscribe reliably. This is the same
     endpoint Klaviyo's own embedded forms use. */
  var KLAVIYO_SUBSCRIBE_URL = 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe';

  function handleSignup(form) {
    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      var id = form.dataset.form || 'signup';
      var data = new FormData(form);
      var email = (data.get('email') || '').toString().trim();
      var name = (data.get('name') || '').toString().trim();
      if (!email || !/.+@.+\..+/.test(email)) { showMsg(id, 'Please enter a valid email.', false); return; }

      var listId = (form.dataset.klaviyoListId || '').trim();
      var successMsg = form.dataset.successMessage || 'Thank you. Your invitation is on its way.';

      // Preview mode - list not configured yet (e.g. in the theme editor)
      if (!listId) {
        console.log('[JuroMiru] Klaviyo list not configured - preview mode', { email: email, name: name });
        showMsg(id, successMsg + ' (preview)', true);
        form.reset();
        return;
      }

      showMsg(id, 'Submitting…', true);

      var body = new URLSearchParams();
      body.append('g', listId);
      body.append('email', email);
      if (name) { body.append('$fields', '$first_name'); body.append('$first_name', name); }

      try {
        // No headers set => fetch uses application/x-www-form-urlencoded (a simple
        // request) => no CORS preflight.
        var res = await fetch(KLAVIYO_SUBSCRIBE_URL, { method: 'POST', body: body });
        var json = await res.json().catch(function () { return {}; });
        if (json && json.success) {
          showMsg(id, successMsg, true);
          form.reset();
        } else {
          var err = json && json.errors && json.errors.length ? json.errors[0] : 'Something went wrong. Please try again or email hello@juromiru.com.';
          showMsg(id, err, false);
        }
      } catch (err) {
        showMsg(id, 'Connection error. Please try again later.', false);
      }
    });
  }

  function handleVendorForm(form) {
    if (form.dataset.smoothContactBound === '1') return;
    if (!form.querySelector('[data-smooth-contact="1"]')) return;
    form.dataset.smoothContactBound = '1';

    var shell = form.closest('.vp-form-shell');
    if (!shell) return;

    var frame = shell.querySelector('[data-contact-frame]');
    var success = shell.querySelector('[data-contact-success]');
    var button = form.querySelector('button[type="submit"]');
    var originalButtonText = button ? button.textContent : '';

    if (frame && frame.name) form.setAttribute('target', frame.name);

    function showThankYou() {
      shell.classList.remove('is-submitting');
      shell.classList.add('is-complete');
      form.classList.remove('is-submitting');
      form.classList.add('is-complete');

      if (button) {
        button.textContent = originalButtonText;
        button.removeAttribute('aria-disabled');
      }

      if (success) {
        success.hidden = false;
        requestAnimationFrame(function () {
          success.classList.add('is-visible');
          if (typeof success.focus === 'function') success.focus({ preventScroll: true });
        });
      }

      window.setTimeout(function () {
        form.hidden = true;
        form.setAttribute('aria-hidden', 'true');
      }, 360);
    }

    form.addEventListener('submit', function () {
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

      shell.classList.add('is-submitting');
      form.classList.add('is-submitting');
      if (button) {
        button.textContent = 'Submitting...';
        button.setAttribute('aria-disabled', 'true');
      }

      window.setTimeout(showThankYou, 520);
    });
  }

  function init() {
    initVideo();
    observeReveals();
    document.querySelectorAll('form[data-klaviyo="1"]').forEach(handleSignup);
    document.querySelectorAll('.jm-vendors form.vp-form').forEach(handleVendorForm);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  // Re-init when sections are edited in the theme editor
  document.addEventListener('shopify:section:load', function () { initVideo(); observeReveals(); init(); });
})();
